import FilterButton from '@/components/page-specific/catering/supplier-menu/components/filter-button'
import { SidebarShell } from '@/components/ui/SidebarShell'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { Tune } from '@mui/icons-material'
import { Stack, Typography, Button, Box, Divider, useMediaQuery, useTheme } from '@mui/material'

import dynamic from 'next/dynamic'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { CcAppPromo } from '../state/useCcAppPromo'
import { FilterAccordion } from './FilterAccordion'
import { FilterChip } from './FilterChip'
import { MenuSearchBox } from './MenuSearchBox'
import { BudgetFilter, BudgetFilterValue, FilterCategory } from './types'

// Only eligible users see the card, so qrcode.react stays out of the canteen chunk
const AppPromoCard = dynamic(() => import('./AppPromoCard').then((m) => m.AppPromoCard))

export const canteenMenuFiltersPanelVendorMenuDataFragmentV2 = graphql(`
  fragment CanteenMenuFiltersPanel_VendorMenuDataFragmentV2 on CCVendorMenu {
    id
    allItems: menuItems(limit: 10) {
      cacheKeys
      pageInfo {
        count
      }
      stats {
        cacheKey
        id
        allergens {
          id
          label
          name
          value
        }
        dietaries {
          id
          label
          name
          value
        }
      }
    }
    vendor {
      id
      companyName
      logo
      images {
        secureUrl
      }
    }
  }
`)

const goalList = [
  { value: 'vegan', displayName: 'dietaries:vegan' },
  { value: 'vegetarian', displayName: 'dietaries:vegetarian' },
  { value: 'powerplant', displayName: 'dietaries:powerplant' },
  { value: 'lowcarb', displayName: 'dietaries:lowcarb' },
  { value: 'highprotein', displayName: 'dietaries:highprotein' },
  { value: 'isPromoted', displayName: 'dietaries:isPromoted' }, // corresponds to 'Gut health'
]

const dietaryList = [
  { value: 'halal', displayName: 'dietaries:halal' },
  { value: 'refined_sugar', displayName: 'dietaries:refined_sugar' },
  { value: 'cereals', displayName: 'dietaries:cereals' },
  { value: 'nuts', displayName: 'dietaries:nuts' },
  // "No added dairy" filter intentionally duplicated with allergens for UI only
  { value: 'milk', displayName: 'allergens:milk' },
]

const allergenList = [
  { value: 'crustaceans', displayName: 'allergens:crustaceans' },
  { value: 'milk', displayName: 'allergens:milk' },
  { value: 'fish', displayName: 'allergens:fish' },
  { value: 'peanuts', displayName: 'allergens:peanuts' },
  { value: 'sesame', displayName: 'allergens:sesame' },
  { value: 'eggs', displayName: 'allergens:eggs' },
  { value: 'soybeans', displayName: 'allergens:soybeans' },
  { value: 'celery', displayName: 'allergens:celery' },
  { value: 'mustard', displayName: 'allergens:mustard' },
  { value: 'sulphur', displayName: 'allergens:sulphur' },
  { value: 'molluscs', displayName: 'allergens:molluscs' },
  { value: 'lupin', displayName: 'allergens:lupin' },
]

type Props = {
  menuData?: FragmentType<typeof canteenMenuFiltersPanelVendorMenuDataFragmentV2>[]
  loading: boolean
  menuFilters: { dietaries: string[]; allergens: string[]; vendors: string[] }
  filteredMenuFilterCount: Record<string, any>
  searchTerm: string
  budgetFilter: BudgetFilter
  onToggleBudgetFilter: (value: BudgetFilterValue) => void
  onSearchChange: (value: string) => void
  onToggleFilterValue: (category: FilterCategory, value: string) => void
  onClearFilters: () => void
  onHideFiltersDrawer?: () => void
  shouldShowFilters: boolean
  hideCCPrices: boolean
  appPromo?: CcAppPromo | null
}

const FiltersPanelMuiV2: React.FC<Props> = ({
  menuData: menuDataProp,
  loading,
  menuFilters,
  filteredMenuFilterCount,
  searchTerm,
  budgetFilter,
  onToggleBudgetFilter,
  onSearchChange,
  onToggleFilterValue,
  onClearFilters,
  onHideFiltersDrawer,
  shouldShowFilters,
  hideCCPrices,
  appPromo,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const menuData = useFragment(canteenMenuFiltersPanelVendorMenuDataFragmentV2, menuDataProp)
  const useCanteenMenuCreditFilter = useFeatureFlag(SPLITS.USE_CANTEEN_MENU_CREDIT_FILTER)

  const handleFilterChange = (category: FilterCategory, value: string) => {
    // override for 'dietary' milk, nut and cereals filters, they use allergens instead
    if (['milk', 'nuts', 'cereals'].includes(value)) {
      onToggleFilterValue('allergens', value)
    } else {
      onToggleFilterValue(category, value)
    }
  }

  const handleRemoveFilter = (category: FilterCategory, value: string) => {
    onToggleFilterValue(category, value)
  }

  const handleRemoveBudgetFilter = () => {
    if (budgetFilter) onToggleBudgetFilter(budgetFilter)
  }

  const handleClearAll = () => {
    onClearFilters()
  }

  const selectedDietaries = useMemo(() => {
    return menuFilters.dietaries.map((dietary) => {
      return {
        label: t(`dietaries:${dietary}`),
        value: dietary,
        displayName: t(`dietaries:${dietary}`),
      }
    })
  }, [menuFilters.dietaries, t])

  const selectedAllergens = useMemo(() => {
    return menuFilters.allergens.map((allergen) => {
      return {
        label: ['nuts', 'cereals'].includes(allergen)
          ? t(`dietaries:${allergen}`)
          : t(`allergens:${allergen}`),
        value: allergen,
      }
    })
  }, [menuFilters.allergens, t])

  const selectedVendors = useMemo(() => {
    return menuFilters.vendors.map((vendorId) => {
      const matchingMenu = menuData?.find((menu) => menu.vendor.id === vendorId)
      return {
        label: matchingMenu ? matchingMenu.vendor.companyName : vendorId,
        value: vendorId,
      }
    })
  }, [menuFilters.vendors, menuData])

  const selectedBudget = useMemo(() => {
    if (!budgetFilter) return []
    return [
      {
        label:
          budgetFilter === 'subsidy'
            ? t('cc:filterLabel_withinSubsidyOnly')
            : t('cc:filterLabel_withinSubsidyAndCredits'),
        value: budgetFilter,
      },
    ]
  }, [budgetFilter, t])

  return (
    <SidebarShell ariaLabel="Canteen filters" sticky={false}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          scrollbarWidth: 'thin',
          py: 1,
          px: 2,
        }}>
        <Stack spacing={2} direction="column">
          <Stack spacing={1}>
            {isMobile && (
              <>
                <Box pt={1}>
                  <FilterButton
                    onClick={onHideFiltersDrawer}
                    startIcon={<Tune fontSize="small" />}
                    variant="white">
                    Hide Filters
                  </FilterButton>
                </Box>
                <Divider />
              </>
            )}
            <MenuSearchBox
              value={searchTerm}
              onChange={(value) => {
                onSearchChange(value)
              }}
            />
          </Stack>
          {appPromo && (
            <AppPromoCard
              iosUrl={appPromo.iosUrl}
              androidUrl={appPromo.androidUrl}
              onDismiss={appPromo.dismiss}
            />
          )}
          {(selectedDietaries.length > 0 ||
            selectedAllergens.length > 0 ||
            selectedVendors.length > 0 ||
            selectedBudget.length > 0) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%">
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {`${t('marketplace:activeFilters')}`}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  disabled={loading}
                  onClick={() => {
                    handleClearAll()
                  }}>
                  {t('marketplace:clearAll')}
                </Button>
              </Stack>
              {[...selectedBudget].map(({ label, value }) => (
                <FilterChip
                  key={`filter-chip-${value}`}
                  label={label}
                  onDelete={handleRemoveBudgetFilter}
                />
              ))}
              {[...selectedVendors].map(({ label, value }) => (
                <FilterChip
                  key={`filter-chip-${value}`}
                  label={label}
                  onDelete={() => handleRemoveFilter('vendors', value)}
                />
              ))}
              {[...selectedAllergens].map(({ label, value }) => (
                <FilterChip
                  key={`filter-chip-${value}`}
                  label={label}
                  onDelete={() => handleRemoveFilter('allergens', value)}
                />
              ))}
              {[...selectedDietaries].map(({ label, value }) => (
                <FilterChip
                  key={`filter-chip-${value}`}
                  label={label}
                  onDelete={() => handleRemoveFilter('dietaries', value)}
                />
              ))}
            </Box>
          )}
          {!hideCCPrices && useCanteenMenuCreditFilter && (
            <FilterAccordion
              label={t('cc:filterLabel_budget')}
              category="budget"
              filterOptions={[
                {
                  value: 'subsidy',
                  selected: budgetFilter === 'subsidy',
                  quantity: filteredMenuFilterCount.withinSubsidy || 0,
                  displayName: t('cc:filterLabel_withinSubsidyOnly'),
                },
                {
                  value: 'subsidyAndCredits',
                  selected: budgetFilter === 'subsidyAndCredits',
                  quantity: filteredMenuFilterCount.withinSubsidyAndCredits || 0,
                  displayName: t('cc:filterLabel_withinSubsidyAndCredits'),
                },
              ]}
              onFilterChange={(_, value) =>
                onToggleBudgetFilter(value as BudgetFilterValue)
              }
            />
          )}
          <FilterAccordion
            label={t('cc:filterVendors')}
            category="vendors"
            filterOptions={
              !shouldShowFilters
                ? []
                : menuData?.map((menu) => ({
                    selected: selectedVendors.map((v) => v.value)?.includes(menu.vendor.id),
                    quantity: filteredMenuFilterCount.vendors[menu.vendor.id] || 0,
                    displayName: menu.vendor.companyName,
                    value: menu.vendor.id,
                    logoUrl: menu.vendor.logo || null,
                  })) || []
            }
            onFilterChange={(category, value) => handleFilterChange(category as FilterCategory, value)}
          />

          <FilterAccordion
            label={t('cc:filterLabel_goal')}
            category="dietaries"
            filterOptions={
              !shouldShowFilters
                ? []
                : goalList.map((g) => ({
                    value: g.value,
                    selected: selectedDietaries.map((s) => s.value).includes(g.value),
                    quantity: filteredMenuFilterCount[g.value] || 0,
                    displayName: t(g.displayName),
                  }))
            }
            onFilterChange={(category, value) => handleFilterChange(category as FilterCategory, value)}
          />

          <FilterAccordion
            label={t('cc:filterLabel_dietaries')}
            category="dietaries"
            filterOptions={
              !shouldShowFilters
                ? []
                : dietaryList.map((d) => ({
                    value: d.value,
                    selected: ['milk', 'nuts', 'cereals'].includes(d.value)
                      ? selectedAllergens.map((s) => s.value).includes(d.value)
                      : selectedDietaries.map((s) => s.value).includes(d.value),
                    quantity: filteredMenuFilterCount[d.value] || 0,
                    displayName: t(d.displayName),
                  }))
            }
            onFilterChange={(category, value) => handleFilterChange(category as FilterCategory, value)}
          />

          <FilterAccordion
            label={t('cc:filterLabel_allergens')}
            category="allergens"
            filterOptions={
              !shouldShowFilters
                ? []
                : allergenList.map((a) => ({
                    value: a.value,
                    selected: selectedAllergens.map((s) => s.value).includes(a.value),
                    quantity: filteredMenuFilterCount[a.value] || 0,
                    displayName: t(a.displayName),
                  }))
            }
            onFilterChange={(category, value) => handleFilterChange(category as FilterCategory, value)}
          />
        </Stack>
      </Box>
    </SidebarShell>
  )
}

export default FiltersPanelMuiV2
